import { type CSSProperties, type PointerEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLanguage, LOCALE_TAG } from '@/i18n';
import type { Booking, Clinic } from '@/types/api';
import {
  atMinute,
  dayBlocks,
  isOpenSlot,
  isoWeekday,
  minutesOfDay,
  SLOT_MIN,
  sameDay,
  snap,
  weekDays,
} from '@/utils/calendar';

/** Height of one 30-minute slot, px: a 30-minute booking fits two lines. */
const SLOT_H = 40;
/** Phones: taller slots, an easier target for a finger. */
const SLOT_H_PHONE = 56;
// TODO(dedupe): same phone breakpoint as Dropdown.tsx (and app.css); share it on a third use.
const PHONE = '(max-width: 640px)';
const MAX_DURATION = 240;
const DAY_END = 24 * 60;
/** How often the current-time dot moves. */
const NOW_TICK_MS = 30_000;
/** Pointer travel (px) before a press on a booking becomes a drag. */
const DRAG_START_PX = 6;
/** On touch a booking is picked up by holding it: a quick swipe scrolls the calendar. */
const LONG_PRESS_MS = 350;

export type BookingChange = { startsAt: string; durationMin: number };

type Props = {
  clinic: Pick<Clinic, 'workingHours' | 'calendarStart' | 'calendarEnd' | 'showWeekend'>;
  weekStart: Date;
  bookings: Booking[];
  /** Nothing can be booked or moved (offline). */
  readOnly?: boolean;
  /** Each booking names its doctor (all doctors shown, not one). */
  showDoctor: boolean;
  onSlot: (start: Date) => void;
  onOpen: (booking: Booking) => void;
  onChange: (booking: Booking, change: BookingChange) => void;
};

type Drag = {
  booking: Booking;
  mode: 'move' | 'resize';
  /** Minutes between the booking's start and where it was grabbed. */
  grab: number;
  x0: number;
  y0: number;
  moved: boolean;
  /** Mouse: at once; touch: after the long press. */
  armed: boolean;
  timer?: number;
  day: number;
  minute: number;
  duration: number;
};

const clock = (minute: number) =>
  `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** The hours shown: the calendar range, widened to bookings outside it (still allowed). */
function visibleRange(clinic: Props['clinic'], bookings: Booking[]): [number, number] {
  let start = clinic.calendarStart * 60;
  let end = clinic.calendarEnd * 60;
  for (const b of bookings) {
    const m = minutesOfDay(new Date(b.startsAt));
    start = Math.min(start, Math.floor(m / SLOT_MIN) * SLOT_MIN);
    end = Math.max(end, Math.min(DAY_END, Math.ceil((m + b.durationMin) / SLOT_MIN) * SLOT_MIN));
  }
  return [start, end];
}

/** Which day column the pointer is over (they carry data-day). */
function dayUnder(x: number, y: number): { index: number; top: number } | null {
  for (const el of document.elementsFromPoint(x, y)) {
    if (el instanceof HTMLElement && el.dataset.day !== undefined) {
      return { index: Number(el.dataset.day), top: el.getBoundingClientRect().top };
    }
  }
  return null;
}

/** The time now, moved on every NOW_TICK_MS. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), NOW_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/** Whether the screen is a phone's (follows rotation and resizing). */
function usePhone(): boolean {
  const [phone, setPhone] = useState(() => window.matchMedia(PHONE).matches);
  useEffect(() => {
    const query = window.matchMedia(PHONE);
    const update = () => setPhone(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return phone;
}

/**
 * Week view: one column per day, 30-minute slots, opening hours shaded open.
 * Tap an empty slot to book; drag a booking to move it, its bottom edge to
 * make it longer. Several people can share a slot (side by side).
 */
export function WeekCalendar(p: Props) {
  const { t } = useTranslation();
  const days = weekDays(p.weekStart, p.clinic.showWeekend);
  const [rangeStart, rangeEnd] = visibleRange(p.clinic, p.bookings);
  const slots: number[] = [];
  for (let m = rangeStart; m < rangeEnd; m += SLOT_MIN) slots.push(m);
  const drag = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<Drag | null>(null);
  const slotH = usePhone() ? SLOT_H_PHONE : SLOT_H;
  const dayFmt = new Intl.DateTimeFormat(LOCALE_TAG[currentLanguage()], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const today = useNow();
  const nowMinute = minutesOfDay(today);
  // The current time, when it falls in the hours shown (its day must be in the week, too).
  const nowTop =
    nowMinute >= rangeStart && nowMinute < rangeEnd
      ? ((nowMinute - rangeStart) / SLOT_MIN) * slotH
      : null;

  // The grid holds the pointer capture and the handlers: the preview of a booking
  // dragged to another day is a new element in that column (see dayBlocks).
  const grid = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  /** A tap just opened a booking: its click must not land in the sheet that opened. */
  const opened = useRef(false);

  // Phones show a few days at a time: a week opens at today (other weeks at their first day).
  const week = p.weekStart.getTime();
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll again when another week is shown
  useEffect(() => {
    const el = box.current;
    const head = el?.querySelector('.cal__day-head.is-today');
    const corner = el?.querySelector('.cal__corner');
    if (!(el && grid.current)) return;
    el.scrollLeft =
      head && corner
        ? head.getBoundingClientRect().left -
          grid.current.getBoundingClientRect().left -
          corner.getBoundingClientRect().width
        : 0;
  }, [week]);

  const stop = () => {
    if (drag.current?.timer) clearTimeout(drag.current.timer);
    drag.current = null;
    setPreview(null);
  };

  // Once a touch drag is armed, the finger moves the booking, not the page.
  useEffect(() => {
    const el = grid.current;
    if (!el) return;
    const block = (e: TouchEvent) => {
      if (drag.current?.armed) e.preventDefault();
    };
    // The click that follows a tap hits whatever is under the finger by then: the sheet's fields.
    const tap = (e: TouchEvent) => {
      if (!opened.current) return;
      opened.current = false;
      e.preventDefault();
    };
    el.addEventListener('touchmove', block, { passive: false });
    el.addEventListener('touchend', tap, { passive: false });
    return () => {
      el.removeEventListener('touchmove', block);
      el.removeEventListener('touchend', tap);
    };
  }, []);

  const begin = (
    e: PointerEvent<HTMLElement>,
    booking: Booking,
    mode: Drag['mode'],
    day: number
  ) => {
    const mouse = e.pointerType === 'mouse';
    if (p.readOnly || (mouse && e.button !== 0)) return;
    e.stopPropagation();
    const column = dayUnder(e.clientX, e.clientY);
    const start = minutesOfDay(new Date(booking.startsAt));
    const pointer = column ? (e.clientY - column.top) / (slotH / SLOT_MIN) + rangeStart : start;
    const d: Drag = {
      booking,
      mode,
      grab: pointer - start,
      x0: e.clientX,
      y0: e.clientY,
      moved: false,
      armed: mouse,
      day,
      minute: start,
      duration: booking.durationMin,
    };
    const { pointerId } = e;
    if (mouse) {
      grid.current?.setPointerCapture(pointerId);
    } else {
      d.timer = window.setTimeout(() => {
        if (drag.current !== d) return;
        d.armed = true;
        grid.current?.setPointerCapture(pointerId);
        navigator.vibrate?.(15);
        setPreview({ ...d });
      }, LONG_PRESS_MS);
    }
    drag.current = d;
  };

  const move = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const far = Math.hypot(e.clientX - d.x0, e.clientY - d.y0) >= DRAG_START_PX;
    if (!d.armed) {
      // A swipe before the long press: the calendar scrolls, nothing moves.
      if (far) stop();
      return;
    }
    if (!(d.moved || far)) return;
    d.moved = true;
    const column = dayUnder(e.clientX, e.clientY);
    if (!column) return;
    const at = (e.clientY - column.top) / (slotH / SLOT_MIN) + rangeStart;
    if (d.mode === 'move') {
      d.day = column.index;
      d.minute = clamp(snap(at - d.grab), 0, DAY_END - d.duration);
    } else {
      d.duration = clamp(snap(at - d.minute), SLOT_MIN, Math.min(MAX_DURATION, DAY_END - d.minute));
    }
    setPreview({ ...d });
  };

  const end = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current;
    stop();
    if (!d) return;
    if (!d.moved) {
      opened.current = e.pointerType === 'touch';
      p.onOpen(d.booking);
      return;
    }
    const day = days[d.day];
    if (!day) return;
    const startsAt = atMinute(day, d.minute).toISOString();
    if (
      startsAt !== new Date(d.booking.startsAt).toISOString() ||
      d.duration !== d.booking.durationMin
    ) {
      p.onChange(d.booking, { startsAt, durationMin: d.duration });
    }
  };

  const style = {
    '--cal-days': days.length,
    '--slot-h': `${slotH}px`,
    '--cal-gutter': '18px',
  } as CSSProperties;
  return (
    <div className="cal" style={style} ref={box}>
      <div
        ref={grid}
        className="cal__grid"
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={stop}
      >
        <div className="cal__corner" />
        {days.map((day) => (
          <div
            key={day.toISOString()}
            className={`cal__day-head ${sameDay(day, today) ? 'is-today' : ''}`}
          >
            {dayFmt.format(day)}
          </div>
        ))}

        <div className="cal__times" aria-hidden="true">
          {slots.map((m) => (
            <div key={m} className="cal__time">
              {m % 60 === 0 ? clock(m) : ''}
            </div>
          ))}
          {nowTop !== null && days.some((d) => sameDay(d, today)) && (
            <span className="cal__now-time" style={{ top: nowTop }}>
              {clock(nowMinute)}
            </span>
          )}
        </div>

        {days.map((day, index) => {
          const weekday = isoWeekday(day);
          const own = p.bookings.filter((b) => sameDay(new Date(b.startsAt), day));
          const items = own.map((b) => {
            const start = minutesOfDay(new Date(b.startsAt));
            return { id: b.id, booking: b, start, end: start + b.durationMin };
          });
          const blocks = dayBlocks(
            items,
            preview && {
              item: {
                id: preview.booking.id,
                booking: preview.booking,
                start: preview.minute,
                end: preview.minute + preview.duration,
              },
              here: preview.day === index,
            }
          );
          return (
            <div key={day.toISOString()} className="cal__day" data-day={index}>
              {slots.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`cal__slot ${isOpenSlot(p.clinic.workingHours, weekday, m) ? 'is-open' : ''}`}
                  disabled={p.readOnly}
                  aria-label={t('calendar.bookAt', { day: dayFmt.format(day), time: clock(m) })}
                  onClick={() => p.onSlot(atMinute(day, m))}
                />
              ))}
              {blocks.map(({ item, col, cols, key, preview: dragging, ghost }) => {
                return (
                  // biome-ignore lint/a11y/useSemanticElements: draggable block with a resize handle inside; a <button> cannot hold it
                  <div
                    key={key}
                    className={`cal__booking ${dragging ? 'is-dragging' : ''} ${ghost ? 'is-drag-source' : ''}`}
                    aria-hidden={ghost || undefined}
                    style={{
                      top: ((item.start - rangeStart) / SLOT_MIN) * slotH,
                      height: ((item.end - item.start) / SLOT_MIN) * slotH - 2,
                      // A strip on the right stays free: tapping it books one more person into the slot.
                      left: `calc((100% - var(--cal-gutter)) * ${col / cols} + 2px)`,
                      width: `calc((100% - var(--cal-gutter)) / ${cols} - 4px)`,
                    }}
                    role="button"
                    tabIndex={ghost ? -1 : 0}
                    aria-label={`${clock(item.start)}–${clock(item.end)} ${item.booking.name}`}
                    onPointerDown={(e) => begin(e, item.booking, 'move', index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        p.onOpen(item.booking);
                      }
                    }}
                  >
                    <span className="cal__booking-line">
                      <span className="cal__booking-time">{clock(item.start)}</span>{' '}
                      <strong>{item.booking.name}</strong>
                    </span>
                    {p.showDoctor && item.booking.dentist && (
                      <span className="cal__booking-meta">{item.booking.dentist.name}</span>
                    )}
                    {!p.readOnly && (
                      <span
                        className="cal__resize"
                        aria-hidden="true"
                        onPointerDown={(e) => begin(e, item.booking, 'resize', index)}
                      />
                    )}
                  </div>
                );
              })}
              {nowTop !== null && sameDay(day, today) && (
                <div className="cal__now" style={{ top: nowTop }} aria-hidden="true" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
